import { Alert, Box,Button, Grid, Modal, Typography } from "@mui/material";
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InputOnlyField, Text, ChosenThemeActionContext, FormSubmitButton, Select, FormsFeatureContext,DatePicker, Numeric, IValidationError } from '@ngt/forms';
import { useFormState, useScopedField, useFormActions, Condition, ConditionMode } from '@ngt/forms-core';
import { Dispatch, FC, SetStateAction, useCallback, useContext, useEffect, useRef, useState} from "react";
import * as Dtos from "../../api/dtos";
import getCreditCardErrorMessage from "@/utils/getCreditCardErrorMessage";
import StripePayment from "../payment/StripePayment";
import { enqueueSnackbar } from "notistack";
import PaymentInPerson from "../payment/PaymentInPerson";

interface IAdminPaymentFormProps {
    open?: { index: number; isEdit: boolean; validationPass: boolean;};
    setOpen: Dispatch<SetStateAction<{ index: number; isEdit: boolean; validationPass: boolean; } | undefined>>;
    amountOwing: number;
    attemptNumber: number;
}

const FieldUpdater: FC<{ index: number, amountOwing: number }> = ({ index, amountOwing }) => {
    const subscription = {
        value: true
    };

    const {
        state: {
            value: paymentOption
        },
        context: {
            setValue: setPaymentOption
        }
    } = useScopedField<number>(`payments[${index}].paymentOption`, subscription);

    const { context: { setValue: setPaymentType }} = useScopedField<number>(`payments[${index}].paymentType`, {});

    useEffect(() => {
        setPaymentType(paymentOption);
    }, [paymentOption, setPaymentType]);

    useEffect(() => {
        if (paymentOption == undefined) {
            setPaymentOption(Dtos.AdminPaymentType.StripeApp);
        }
    }, [paymentOption, setPaymentOption])

    const {
        state: {
            value: amountPaid
        },
        context: {
            setValue: setAmountPaid
        }
    } = useScopedField<number>(`payments[${index}].amountPaid`, subscription);

    useEffect(() => {
        if (amountPaid === undefined) {
            setAmountPaid(amountOwing);
        }
    }, [])



    return null;
}

interface IPaymentProps {
    handleStripePaymentComplete: (clientReferenceId?: string) => void;
    handlePaymentCancel: () => void;
    paymentAmount: number;
}

const Payment: FC<IPaymentProps> = ({
    handleStripePaymentComplete,
    handlePaymentCancel,
    paymentAmount
}) => {
    const {state: { value: email }} = useScopedField<string>("email", { value: true });

    return (
        <StripePayment 
            handleStripePaymentComplete={handleStripePaymentComplete} 
            handleCancel={handlePaymentCancel}
            cost={paymentAmount ?? 0} 
            email={email ?? undefined} 
        />
    )
}

const PaymentError: FC<{index: number}> = ({index}) => {

    const { state: { value: paymentSuccess } } = useScopedField<boolean>(`payments[${index}].paymentSuccess`, {value: true});
    const { state: { value: paymentStatus } } = useScopedField<Dtos.CommWebResponseCode>(`payments[${index}].paymentStatus`, {value: true});
    const { state: { value: paymentMessage } } = useScopedField<string>(`payments[${index}].paymentMessage`, {value: true});
    const { state: { value: paymentFailure } } = useScopedField<boolean>(`paymentFailure`, {value: true});
    
    
    if (paymentSuccess !== false && !paymentFailure) {
        return null;
    }

    const error = { 
        responseStatus: {
            message: paymentStatus ? getCreditCardErrorMessage(paymentStatus) :  "An error occured while trying to save the form"
        } as Dtos.ResponseStatus
    };

    return (

        <Alert severity="error" sx={{ width: '100%' }}>
            {error ? error.responseStatus.message : "An error occured while trying to save the form"}
            {paymentMessage && (
                <Box sx={{pt: 2}}>
                    <strong>Bank Message:</strong> {paymentMessage}
                </Box>
            )}
        </Alert>

    )
}

const AdminPaymentForm: FC<IAdminPaymentFormProps> = ({ open, setOpen, amountOwing }) => {
    const setChosenTheme = useContext(ChosenThemeActionContext);
    const { context: { getValue: getPayments, setValue: setPayments }} = useScopedField<Dtos.Payment[]>('payments', {});
    const { context: { getValue: getRegId }} = useScopedField<number>('id', {});
    const {state: {value: regId}} = useScopedField<number>("id", {value: true});
    const actions = useFormActions<any, IValidationError>();
    const {fields, errors, validating, submitting } = useFormState({errors: true, fields: true, validating: true, submitting: true})


    const [openPaymentModal, setOpenPaymentModal] = useState(false);
    const [openPaymentModalInPerson, setOpenPaymentModalInPerson] = useState(false);
    const [inputPaymentAmount, setInputPaymentAmount] = useState(0);
    
    const hasErrors = fields.reduce((a, b) => {
        if (b === 'payments') {
            return false
        }
        if(a)
            return a;
        return errors[b]?.length > 0;
    }, false);

    const isEdit = open?.isEdit ?? false;
    
    useEffect(() => {
        setChosenTheme('light');
    }, []);
    
    const payments = getPayments();
    const paymentIndex = open?.index ?? 0;

    const [previousState, setPreviousState] = useState<Dtos.Payment | undefined>(undefined);

    useEffect(() => {
        if (open?.index !== undefined) {
            setPreviousState(getPayments()?.[open.index])
        }
        else {
            setPreviousState(undefined)
        }
    }, [open?.index, getPayments, setPayments])

    const handleCancel = () => {
        if (!isEdit)
        {
            setPayments(current => current?.filter((x, i) => i !== paymentIndex) ?? [], true, true, true, false)
        }
        else {
            setPayments(current => {
                if (!previousState) {
                    return current
                }

                if (!current) {
                    return current;
                }

                if (current.length <= paymentIndex) {
                    return current;
                }

                const newData = [
                    ...current
                ];

                newData[paymentIndex] = previousState;

                return newData;
            }, true, true, true, false)
        }

        setOpen(undefined);
        setPreviousState(undefined)
        actions.setFieldValue('submitTriggeredByPayment', false, true, true, true, false);
        actions.setFieldValue('paymentFailure', false, true, true, true, false);
    }



    const paymentStateRef = useRef<{ paymentComplete: (clientReferenceId?: string) => void, paymentFailure: (error: any) => void }>();
    
    const awaitPayment = useCallback(() => {
        return new Promise<string | undefined>((resolve, reject) => {
            paymentStateRef.current = {
                paymentComplete: (clientReferenceId) => {
                    resolve(clientReferenceId);
                    paymentStateRef.current = undefined;
                },
                paymentFailure: (error) => {
                    reject(error);
                    paymentStateRef.current = undefined;
                }
            }
        });
    }, [paymentStateRef]);

    const handleStripePaymentComplete = useCallback((clientReferenceId?: string) => {

        setOpenPaymentModal(false);

        if (paymentStateRef.current) {
            if (clientReferenceId) {
                paymentStateRef.current.paymentComplete(clientReferenceId);
            }
            else {
                paymentStateRef.current.paymentFailure("clientReferenceId not found!");
            }
        }

        // verify it somehow

        // if it is successful then save the form and add the payment into the database
        

        
    }, [setOpenPaymentModal, paymentStateRef])

    const handleInPersonPaymentComplete = useCallback((clientReferenceId?: string) => {

        setOpenPaymentModalInPerson(false);

        if (paymentStateRef.current) {
            if (clientReferenceId) {
                paymentStateRef.current.paymentComplete(clientReferenceId);
            }
            else {
                paymentStateRef.current.paymentFailure("clientReferenceId not found!");
            }
        }
        
    }, [setOpenPaymentModal, paymentStateRef])

    const handleSubmit = async (event: any) => {
        actions.setFieldValue('paymentFailure', false, true, true, true, false);

        const errors = actions.getErrors();

        let hasErrors = false;

        for (let propertyPath of Object.keys(errors)) {
            if (!propertyPath.startsWith(`payments[${paymentIndex}]`)) {
                continue;
            }

            if (!errors[propertyPath] || errors[propertyPath].length === 0) {
                continue;
            }

            actions.setFieldTouched(propertyPath, true, true);

            hasErrors = true;
        }

        if (hasErrors) {
            return;
        }

        actions.setFieldValue('submitTriggeredByPayment', true, true, true, true, false);
        setPayments(current => {current.forEach(p => p.paymentType = p.paymentOption); return current});

        if (payments?.[paymentIndex]?.amountPaid !== undefined) // because ya know typescript
        {
            if (payments?.[paymentIndex]?.paymentOption === (Dtos.AdminPaymentType.Card as number) && payments[paymentIndex].amountPaid! > 0) {
                setInputPaymentAmount(payments?.[paymentIndex]?.amountPaid ?? 0);
    
                setOpenPaymentModal(true);
                try {
                    const clientReferenceId = await awaitPayment();
    
                    if (!clientReferenceId) {
                        // cancelled.
                        return;
                    } 
    
                    actions.setFieldValue(`payments[${paymentIndex}].receiptNumber`, clientReferenceId, true, true, true, false);
                }
                catch (error) {
                    // do something cause stuff done broke.
                    return;
                }
            }
            if (payments?.[paymentIndex]?.paymentOption === (Dtos.AdminPaymentType.StripeApp as number) && payments[paymentIndex].amountPaid! > 0) {
                setInputPaymentAmount(payments?.[paymentIndex]?.amountPaid ?? 0);
    
                setOpenPaymentModalInPerson(true);
                try {
                    const clientReferenceId = await awaitPayment();
    
                    if (!clientReferenceId) {
                        // cancelled.
                        return;
                    } 
    
                    actions.setFieldValue(`payments[${paymentIndex}].receiptNumber`, clientReferenceId, true, true, true, false);
                }
                catch (error) {
                    // do something cause stuff done broke.
                    return;
                }
            }
        }

        actions.submit();
    }

    const showModal = open?.validationPass == true;
    
    useEffect(() => {
        const submitTriggeredByPayment = actions.getFieldValue<boolean>('submitTriggeredByPayment')
        const paymentFailure = actions.getFieldValue<boolean>('paymentFailure')

        if (!submitting && showModal && submitTriggeredByPayment) {
            if (paymentIndex) {
                const payments = getPayments();

                const payment = payments?.[paymentIndex];

                if (payment && payment.paymentType === 3 && (payment.paymentSuccess === false || paymentFailure)) {
                    return;
                }
            }

            setOpen(undefined);
            actions.setFieldValue('submitTriggeredByPayment', false, true, true, true, false);
        }
    }, [submitting, actions.getFieldValue, actions.setFieldValue, getPayments, setOpen, showModal, paymentIndex])

    return (
        <>
            <Grid 
                item
                xs={12} 
                sx={{
                    display: 'flex',
                    justifyContent: 'end',
                    mt: 1
                }}
            >
                <Button
                    variant="outlined"
                    onClick={() => {
                        if (!!hasErrors) {
                            enqueueSnackbar("Please fix registration errors before adding payment", { variant: 'error' });
                            // isplay error message
                            return;
                        } 
                        else if (!(regId !== null && regId !== undefined) ) {
                            enqueueSnackbar("The registration Id does not exist", { variant: 'error' });
                            // display error message
                            return;
                        }
                        else
                        {
                            setOpen({ index: getPayments()?.length ?? 0, isEdit: false, validationPass: !hasErrors && (regId !== null && regId !== undefined) });
                        }
                    }}
                >
                    <FontAwesomeIcon size="2x" icon={faPlus} />
                </Button>
            </Grid>
            
        <Modal
            open={open?.validationPass == true}
            onClose={() => {}}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box 
                sx={{
                    position: 'absolute' as 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 600,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                }}
            >
                    {open?.validationPass == true && <FieldUpdater index={paymentIndex} amountOwing={amountOwing} />}
                    <Grid  
                        container
                    >
                        <Grid 
                            item
                            xs={12}
                        >
                            <Typography
                                variant="h2"
                            >

                                Payment
                            </Typography>
                            {
                                payments?.[paymentIndex]?.receiptNumber && (
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            paddingTop: 2
                                        }}
                                    >

                                        Receipt No: <b>{payments?.[paymentIndex]?.receiptNumber}</b>
                                    </Typography>
                                )
                            }
                            <Box mb={2} >
                                <InputOnlyField
                                    label="Date"
                                    propertyPath={`payments[${paymentIndex}].paymentDateTime`}
                                    component={DatePicker}
                                    slotProps={{
                                        textField: {
                                            variant: "standard",
                                            margin: "normal",
                                        }
                                    }}

                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid 
                        container
                        spacing={4}
                    >
                        <Grid item xs={12}>
                            <Grid container spacing={4}>
                                <Grid
                                    item
                                    xs={12}
                                >
                                    <InputOnlyField
                                        label="Amount"
                                        propertyPath={`payments[${paymentIndex}].amountPaid`}
                                        component={Numeric}
                                        variant="standard"
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                >
                                    <InputOnlyField
                                        label="Payment Type"
                                        propertyPath={`payments[${paymentIndex}].paymentOption`}
                                        component={Select}
                                        FormControlProps={{
                                            variant: "standard"
                                        }}
                                        
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                >
                                    <Condition
                                        condition={({ value }) => value?.payments?.[paymentIndex]?.paymentOption === 1 || value?.payments?.[paymentIndex]?.paymentOption === 2 || value?.payments?.[paymentIndex]?.paymentType === 6}
                                        subscription={{value: true}}
                                        mode={ConditionMode.Enable}
                                    >
                                        <InputOnlyField
                                            label="C/MO/EFT Number"
                                            propertyPath={`payments[${paymentIndex}].cmNumber`}
                                            component={Text}
                                            variant="standard"
                                        />
                                    </Condition>
                                </Grid>
                                <Condition
                                    condition={({ value }) =>value?.payments?.[paymentIndex]?.paymentOption === 1}
                                    subscription={{value: true}}
                                    mode={ConditionMode.Enable}
                                >
                                    <Grid
                                        item
                                        xs={12}
                                    >
                                        <InputOnlyField
                                            label="Bank"
                                            propertyPath={`payments[${paymentIndex}].bank`}
                                            component={Text}
                                            variant="standard"
                                            
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                    >
                                        <InputOnlyField
                                            label="BSB"
                                            propertyPath={`payments[${paymentIndex}].bsb`}
                                            component={Text}
                                            variant="standard"
                                            
                                        />
                                    </Grid>
                                </Condition>
                                
                                <Grid
                                    item
                                    xs={12}
                                >
                                    <InputOnlyField
                                        label="Receipt"
                                        propertyPath={`payments[${paymentIndex}].receiptNumber`}
                                        component={Text}
                                        variant="standard"
                                        
                                        disabled
                                        hidden
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    {
                        showModal && (
                            <Grid 
                                container
                                justifyContent="space-around"
                            >
                                <Grid 
                                    item 
                                    xs={12}
                                    md={12}
                                    sx={{
                                        pt: '32px !important',
                                    }}
                                >
                                    <PaymentError index={paymentIndex} />
                                </Grid>
                            </Grid>
                        )
                    }
                    <Grid 
                        container
                        justifyContent="space-around"
                    >
                        <Grid 
                            item 
                            xs={12}
                            md={6}
                            sx={{
                                pt: '32px !important',
                                display: 'flex',
                                justifyContent: 'center'
                            }}
                        >
                            <FormSubmitButton
                                variant="contained"
                                color="primary"
                                size="large"
                                sx={{
                                    width: {
                                        xs: '100%',
                                        md: 'fit-content'
                                    },
                                    minWidth: '160px',
                                    borderColor: 'secondary.main',
                                    borderRadius: '20px',
                                }}
                                onClick={(e:any)=>handleSubmit(e)}
                            >
                                <Typography
                                    variant="body2"
                                >   
                                    Submit
                                </Typography>
                            </FormSubmitButton>
                        </Grid>
                        <Grid 
                            item 
                            xs={12} 
                            md={6}
                            sx={{
                                pt: '32px !important',
                                display: 'flex',
                                justifyContent: 'center'
                            }}
                        >
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="large"
                                sx={{
                                    width: {
                                        xs: '100%',
                                        md: 'fit-content'
                                    },
                                    minWidth: '160px',
                                    color: 'seconday.main',
                                    borderRadius: '20px',
                                    borderColor: 'seconday.main',
                                }}
                                onClick={handleCancel}
                            >
                                <Typography
                                    variant="body2"
                                >   
                                    Cancel
                                </Typography>
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Modal>
            {
                openPaymentModal &&(
                    <Payment 
                        handleStripePaymentComplete={handleStripePaymentComplete} 
                        handlePaymentCancel={() => {
                            setOpenPaymentModal(false);
                            
                            paymentStateRef.current?.paymentComplete(undefined);
                        }}
                        paymentAmount={inputPaymentAmount}
                    />
                )
            }
            {
                openPaymentModalInPerson &&(
                    <PaymentInPerson 
                        handleInPersonPaymentComplete={handleInPersonPaymentComplete} 
                        handleCancel={() => {
                            setOpenPaymentModalInPerson(false);
                            
                            paymentStateRef.current?.paymentComplete(undefined);
                        }}
                        cost={inputPaymentAmount}
                    />
                    
                )
            }
        </>
    );
};

export default AdminPaymentForm;
